import { useCallback, useEffect, useMemo, useState } from 'react';

import { FramingChoice, FramingProduct, FramingSelections } from '../types';
import { getInitialSelection } from '../utils/framePricing';
import { applyChoiceToSelections } from '../utils/frameSelections';

interface UseFramingSelectionsArgs {
  products: FramingProduct[];
  /** Called whenever the user changes a selection (confirm / continue unframed). */
  onSelectionChange?: (selections: FramingSelections) => void;
}

export interface FramingSelectionsController {
  /** Current framing selection per product id (undefined = not chosen yet). */
  selections: FramingSelections;
  /** Representative product (first of the open group) used to seed the drawer, or null when closed. */
  targetProduct: FramingProduct | null;
  /** Product ids the open drawer was opened for (empty when closed). */
  targetIds: string[];
  isOpen: boolean;
  /**
   * Open the framing drawer for a group of products (one or more that share a
   * choice). See `confirm` for which of them an eventual choice applies to.
   */
  openFor: (productIds: string[]) => void;
  close: () => void;
  /**
   * Commit a choice. `applyToAll` targets every product; otherwise only the
   * first product of the open group, so confirming the multi-work drawer with
   * "apply to all" unticked affects one work and leaves the rest untouched
   * for individual selection.
   */
  confirm: (choice: FramingChoice, applyToAll?: boolean) => void;
}

const buildInitialSelections = (
  products: FramingProduct[],
): FramingSelections =>
  products.reduce<FramingSelections>((acc, product) => {
    acc[product.id] = getInitialSelection(product);
    return acc;
  }, {});

/**
 * Owns per-product framing selections and which product's drawer is open.
 * Reconciles when the product list changes without clobbering existing choices.
 */
export const useFramingSelections = ({
  onSelectionChange,
  products,
}: UseFramingSelectionsArgs): FramingSelectionsController => {
  const [selections, setSelections] = useState<FramingSelections>(() =>
    buildInitialSelections(products),
  );
  const [targetIds, setTargetIds] = useState<string[]>([]);

  // Add/remove keys when the product list changes, and adopt a choice made
  // outside the section (`initialFrame`); otherwise keep existing choices.
  useEffect(() => {
    setSelections((prev) => {
      const next: FramingSelections = {};
      products.forEach((product) => {
        if (product.initialFrame !== undefined) {
          next[product.id] = product.initialFrame;
          return;
        }
        next[product.id] =
          product.id in prev ? prev[product.id] : getInitialSelection(product);
      });
      const sameSize = Object.keys(prev).length === Object.keys(next).length;
      const sameSelections = products.every(
        (product) =>
          product.id in prev && prev[product.id] === next[product.id],
      );
      return sameSize && sameSelections ? prev : next;
    });
  }, [products]);

  const targetProduct = useMemo(
    () => products.find((product) => product.id === targetIds[0]) ?? null,
    [products, targetIds],
  );

  const openFor = useCallback((productIds: string[]) => {
    setTargetIds(productIds);
  }, []);

  const close = useCallback(() => setTargetIds([]), []);

  const confirm = useCallback(
    (choice: FramingChoice, applyToAll = false) => {
      const next = applyChoiceToSelections({
        applyToAll,
        choice,
        previous: selections,
        products,
        targetIds: applyToAll ? targetIds : targetIds.slice(0, 1),
      });

      setSelections(next);
      onSelectionChange?.(next);
      setTargetIds([]);
    },
    [products, selections, targetIds, onSelectionChange],
  );

  return {
    selections,
    targetProduct,
    targetIds,
    isOpen: targetIds.length > 0,
    openFor,
    close,
    confirm,
  };
};
